import { useEffect, useState, type VFC } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { renderProteinAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type InputTableQuerySpec } from "../../graphql.js";
import { type IProteinState, shouldLoadProtein } from "../../redux/modules/protein.js";
import { createTableSpecForProteinParent } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import GenericDataTableContainer from "../core/grid/GenericDataTableContainer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { ChildEntitiesButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";

interface IProteinRelatedEntitiesViewOwnProps {
  subtitle: string;
  relatedEntitiesType: string;
  relatedEntitiesSubType?: number;
  hideCheckboxes?: boolean;
  middleToolbarElement?: JSX.Element;
  expectedEntityType: string;
}

const ProteinRelatedEntitiesView: VFC<IProteinRelatedEntitiesViewOwnProps> = (props) => {
  const { proteinId } = useParams<"proteinId">();
  const { protein } = useSelector<IAppState, IProteinState>((state) => state.protein);
  const viewId = useURLViewID();
  const [tableSpec, setTableSpec] = useState<InputTableQuerySpec>();
  const load = useLoadDispatchWithIdAsNumber(shouldLoadProtein, proteinId);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // When arriving here from another related entity (orthologs, aaFeatures), the relatedEntitiesType may initially still
    // point to the previous one.
    // We should not load data until it was updated. This is checked via expectedEntityType.
    if (protein && props.relatedEntitiesType && props.relatedEntitiesType === props.expectedEntityType) {
      setTableSpec(
        createTableSpecForProteinParent(props.relatedEntitiesType, protein.id, props.relatedEntitiesSubType),
      );
    }
  }, [protein, props.relatedEntitiesType, props.relatedEntitiesSubType, props.expectedEntityType]);

  return (
    <GenericDataTableContainer
      title={protein ? `${protein.sequenceId}` : ""}
      subtitle={props.subtitle}
      tableSpec={tableSpec}
      middleToolbarElement={props.middleToolbarElement}
      rightToolbarElement={
        <ChildEntitiesButtonHeader
          tableSpec={tableSpec}
          getParentEntityName={() => protein?.systematicName}
          hideAnalyzeBtn={false}
        />
      }
      viewId={viewId ?? undefined}
      hideCheckboxes={props.hideCheckboxes}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderProteinAttributesWidgets(protein)}
          key={"protein-related-entities-view-widget-1"}
        />,
      ]}
    />
  );
};

export default ProteinRelatedEntitiesView;
